import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Control, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { IEmailTemplate } from "../../../shared/interfaces/email-template/IEmailTemplate";
import { EEmailCategory } from "../../../shared/interfaces/email-list/IEmailList";
import { ELanguage } from "../../../shared/enums/user/ELanguage";
import { Alert } from "@mui/lab";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
interface IProps {
  control: Control<IEmailTemplate>;
  isUpdate?: boolean;
}
const EmailTemplateForm = ({ control, isUpdate = false }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning">{t("emails.possibleVariables")}</Alert>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name={"name"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("emails.name")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name={"subject"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("emails.subject")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("general.isActive")}
                select
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="true">{t("general.true")}</option>
                <option value="false">{t("general.false")}</option>
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name={"category"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? "Mui-error" : ""}
                  id="category-label"
                >
                  {t("emails.category")}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="category-label"
                  id="category-select"
                  value={value ?? ""}
                  label={t("emails.category")}
                  onChange={onChange}
                >
                  {Object.values(EEmailCategory)?.map((category) => {
                    return (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Controller
            name={"language"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? "Mui-error" : ""}
                  id="language-label"
                >
                  {t("emails.language")}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="language-label"
                  id="language-select"
                  value={value ?? ""}
                  label={t("emails.language")}
                  onChange={onChange}
                >
                  {Object.values(ELanguage)?.map((language) => {
                    return (
                      <MenuItem key={language} value={language}>
                        {t(`language.${language}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="content"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div>
                <ReactQuill
                  theme="snow"
                  value={value ?? ""}
                  onChange={onChange}
                />
                {error && <p style={{ color: "red" }}>{error.message}</p>}
              </div>
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant={"contained"}
          color={"secondary"}
          sx={{ marginTop: "20px" }}
          type={"button"}
          onClick={() => navigate(-1)}
        >
          {t("button.cancel")}
        </Button>
        <Button
          variant={"contained"}
          sx={{ margin: "20px 0 0 10px" }}
          type={"submit"}
        >
          {t(isUpdate ? "button.update" : "button.create")}
        </Button>
      </Box>
    </Card>
  );
};

export default EmailTemplateForm;
